import React from 'react';
import { Stack, Typography } from '@mui/material';
import { ArrowForward, Groups, LocationOn } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Chip, SurfaceCard } from './ConvivialComponents';
import { getRemainingSeats, getTableCity, isTableFull } from '../../utils/tablePresentation';

// Keep table appearance and navigation identical in the list and restaurant pages.
export default function TableListCard({ table: pin }) {
    const navigate = useNavigate();
    const location = pin.location || {};
    const city = getTableCity(pin);
    const parsedStartAt = pin.startAt ? new Date(pin.startAt) : null;
    const startAt = parsedStartAt && !Number.isNaN(parsedStartAt.getTime()) ? parsedStartAt : null;
    const remaining = getRemainingSeats(pin);
    const isFull = isTableFull(pin);
    const interests = pin.interests || [];
    const dateLabel = startAt
        ? `${startAt.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} · ${startAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
        : 'Data da confermare';

    return (
        <SurfaceCard
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/table/${pin.id}`)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') navigate(`/table/${pin.id}`);
            }}
            sx={{
                p: { xs: 0, sm: 0 },
                borderRadius: '17px',
                cursor: 'pointer',
                bgcolor: isFull ? '#eeeeee' : 'background.paper',
                borderColor: isFull ? '#d0d0d0' : 'rgba(223, 191, 193, 0.35)',
                boxShadow: isFull ? 'none' : undefined,
                '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 3 },
            }}
        >
            {/* The cropped logo decorates the cover without covering its text. */}
            <Stack
                direction="row"
                alignItems="flex-end"
                justifyContent="space-between"
                useFlexGap
                flexWrap="wrap"
                spacing={1}
                sx={{
                    minHeight: 56,
                    px: 2.25,
                    py: 1.25,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: isFull ? '#e0e0e0' : 'primary.fixed',
                    color: isFull ? 'text.secondary' : 'primary.main',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: 120,
                        height: 120,
                        right: -12,
                        top: -35,
                        backgroundImage: `url("${process.env.REACT_APP_BASE_PATH || process.env.PUBLIC_URL || ''}/assets/logo.png")`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        opacity: 0.13,
                        filter: isFull ? 'grayscale(1)' : 'none',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', position: 'relative', zIndex: 1, overflowWrap: 'anywhere', flex: '1 1 140px' }}>
                    {interests.length > 0 ? interests.slice(0, 2).join(' & ') : 'Incontro conviviale'}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, position: 'relative', zIndex: 1 }}>
                    {dateLabel}
                </Typography>
            </Stack>
            <Stack spacing={1.25} sx={{ p: 2.25 }}>
                <Typography variant="h3" sx={{ fontSize: 20, lineHeight: 1.4, letterSpacing: '-0.4px', color: isFull ? 'text.secondary' : 'text.primary', overflowWrap: 'anywhere' }}>
                    {pin.name}
                </Typography>
                <Stack spacing={0.5}>
                    <Meta icon={<LocationOn />} text={[city, location.place || 'Location da confermare'].filter(Boolean).join(' · ')} muted={isFull} />
                    <Meta icon={<Groups />} text={`${pin.attendeeCount || 0} / ${pin.limit || '-'} convivial al tavolo`} muted={isFull} />
                </Stack>
                {interests.length > 0 && (
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ '& .MuiChip-root': { borderRadius: '6px', bgcolor: 'background.default', border: 0, height: 'auto', minHeight: 26, maxWidth: '100%' }, '& .MuiChip-label': { fontSize: 11, py: 0.5, whiteSpace: 'normal', overflowWrap: 'anywhere' } }}>
                        {interests.slice(0, 2).map((interest) => (
                            <Chip key={interest}>{interest.trim()}</Chip>
                        ))}
                        {interests.length > 2 && <Chip>+{interests.length - 2}</Chip>}
                    </Stack>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" spacing={1} sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'outline.variant' }}>
                    <Chip sx={{ bgcolor: isFull ? '#d7d7d7' : '#edf4ef', color: isFull ? '#616161' : '#326348', border: 0, height: 28, '& .MuiChip-label': { fontSize: 11, fontWeight: 700 } }}>
                        {isFull ? 'Completo' : remaining === 1 ? 'Ultimo posto' : `${remaining} posti liberi`}
                    </Chip>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: isFull ? 'text.secondary' : 'primary.main' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Scopri il tavolo</Typography>
                        <ArrowForward sx={{ fontSize: 16 }} />
                    </Stack>
                </Stack>
            </Stack>
        </SurfaceCard>
    );
}

function Meta({ icon, text, muted = false }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            {React.cloneElement(icon, { sx: { fontSize: 18, color: muted ? 'text.disabled' : 'primary.main' } })}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{text}</Typography>
        </Stack>
    );
}
